import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "code-samples"
    }}>{`Code Samples`}</h1>
    <p>{`Below you will find various code samples with links to starter projects that expand on them. These should help you get
started quickly with your idea, and help you develop a plugin for Photoshop in UXP.`}</p>
    <p>{`The samples are all available on GitHub `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples"
      }}>{`here`}</a>{` where you can clone or fork them. If you're not familiar with GitHub and just want to download the examples, click on the link above and just click the green `}<inlineCode parentName="p">{`Code`}</inlineCode>{` button, then click "Download ZIP."`}</p>
    <h2 {...{
      "id": "theme-awareness"
    }}>{`Theme Awareness`}</h2>
    <p>{`Photoshop provides light and dark themes. Using Spectrum UXP, you can access the Photoshop colors to make your UI look as native as possible.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`body {
  background-color: var(--uxp-host-background-color);
  color: var(--uxp-host-text-color);
  border-color: var(--uxp-host-border-color);
  font-size: var(--uxp-host-font-size);
}
`}</code></pre>
    <p>{`See `}<a parentName="p" {...{
        "href": "/photoshop/uxp/guides/theme-awareness/"
      }}>{`Theme Awareness`}</a>{` for more details.`}</p>
    <h2 {...{
      "id": "entrypoints"
    }}>{`Entrypoints`}</h2>
    <h3 {...{
      "id": "menu-items"
    }}>{`Menu Items`}</h3>
    <p>{`If your plugin is meant to automate a simple workflow, or you don't need to collect user input, you can expose commands as entry points in your manifest. These kind of plugins will run headless. A "command" type of entry point will define which global scope method in your plugin's JS file should be called when user clicks the menu item.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"entrypoints": [
    {
      "type": "command",
      "id": "writelayers",
      "label": {
        "default": "Export Top Layers..."
      }
    }
  ]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function writelayers() {
  // This method will be called by Photoshop when user clicks "Plugins > <Your Plugin> > Export Top Layers...
}
`}</code></pre>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/direct-action-js-sample"
      }}>{`direct-action-js-sample`}</a>{` as an example that will gather the top layers in the current document, ask user where they want to save, and export them to the folder.`}</p>
    <p>{`Using this method, you can write one plugin with multiple functionalities.`}</p>
    <h3 {...{
      "id": "panels"
    }}>{`Panels`}</h3>
    <p>{`Plugins can present panels that look and feel like the built in panels in Photoshop. `}</p>
    <p>{`Defining a "panel" type entrypoint will cause Photoshop to do two things:`}</p>
    <ul>
      <li parentName="ul">{`Add a menu item for your plugin with the name defined in the `}<inlineCode parentName="li">{`label`}</inlineCode>{` field, based on locale.`}</li>
      <li parentName="ul">{`When menu item is clicked, load the `}<inlineCode parentName="li">{`main`}</inlineCode>{` field in manifest for the contents of the panel.`}</li>
    </ul>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/vanilla-js-sample"
      }}>{`vanilla-js-sample`}</a>{` as an example project.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"entrypoints": [
    {
      "type": "panel",
      "id": "vanilla",
      "label": {
        "default": "Vanilla JS Sample"
      },
      ...
    }
  ]
`}</code></pre>
    <p>{`For all details that go into a panel entrypoint, see `}<a parentName="p" {...{
        "href": "/photoshop/uxp/guides/uxp_guide/uxp-misc/manifest-v4/"
      }}>{`UXP Manifest docs`}</a>{`.`}</p>
    <h3 {...{
      "id": "coming-soon---one-off-scripts"
    }}>{`Coming Soon - One Off Scripts`}</h3>
    <p>{`For those used to ExtendScript jsx files, we are working on one off scripts where Photoshop can be provided a JavaScript
file which will be ran on the fly and then be cleaned up. `}</p>
    <h2 {...{
      "id": "ui-libraries"
    }}>{`UI Libraries`}</h2>
    <p>{`While writing a simple JS/HTML/CSS plugin is possible, those who are building plugins that have internal state management,
or more complicated UI may benefit from commonly used UI libraries. Below examples are not necessarily only ones you can use,
but can serve as good starters if you already are familiar with a library.`}</p>
    <p>{`We do not suggest that you use any particular library for your plugins, but suggest that you read about them and decide
for yourself what will work the best for you.`}</p>
    <h3 {...{
      "id": "react"
    }}>{`React`}</h3>
    <p><a parentName="p" {...{
        "href": "https://reactjs.org/"
      }}>{`React.js`}</a>{` is one of the most used UI libraries. It's declarative and component based, helping you
break your UI into reusable components. `}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-react-starter"
      }}>{`ui-react-starter`}</a>{` for the sample project.`}</p>
    <h3 {...{
      "id": "vue"
    }}>{`Vue`}</h3>
    <p><a parentName="p" {...{
        "href": "https://vuejs.org/"
      }}>{`Vue.js`}</a>{` is another declarative UI library that's easy to learn. `}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-vue-starter"
      }}>{`ui-vue-starter`}</a>{` for the sample project.`}</p>
    <h3 {...{
      "id": "svelte"
    }}>{`Svelte`}</h3>
    <p><a parentName="p" {...{
        "href": "https://svelte.dev/"
      }}>{`Svelte`}</a>{` is different from the other two libraries, in the sense that it compiles your code to a
simple JS application, shrinking it's size.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-svelte-starter"
      }}>{`ui-svelte-starter`}</a>{` for the sample project.`}</p>
    <h2 {...{
      "id": "uxp-core-apis"
    }}>{`UXP Core APIs`}</h2>
    <p>{`While all APIs under `}<inlineCode parentName="p">{`require("photoshop")`}</inlineCode>{` are provided by Photoshop, UXP itself provides core functionality that is shared across
applications, such as file system access, secure storage and web requests.`}</p>
    <h3 {...{
      "id": "file-system-access"
    }}>{`File System Access`}</h3>
    <p>{`With UXP, plugins have access only to their own plugin folder, a data folder and a temporary folder. All other file locations
have to be explicitly given access to by the user. See `}<a parentName="p" {...{
        "href": "/photoshop/uxp/uxp/reference-js/Modules/uxp/Persistent%20File%20Storage/FileSystemProvider/"
      }}>{`FileSystemProvider`}</a>{`
for information on what APIs and folders are available to you.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/jszip-sample"
      }}>{`jszip-sample`}</a>{` for an example of how temporary folder is accessed.`}</p>
    <h3 {...{
      "id": "secure-storage"
    }}>{`Secure Storage`}</h3>
    <p>{`This allows you to store data across sessions. Secure storage APIs are all asynchronous.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const secureStorage = require("uxp").storage.secureStorage;
const value = await secureStorage.getItem("key");
`}</code></pre>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/secure-storage-sample"
      }}>{`secure-storage-sample`}</a>{` for an example use of these APIs.`}</p>
    <h3 {...{
      "id": "web-requests"
    }}>{`Web Requests`}</h3>
    <p>{`UXP provides `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` method globally, matching HTML DOM `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` method. See `}<a parentName="p" {...{
        "href": "/photoshop/uxp/uxp/reference-js/Global%20Members/Data%20Transfers/fetch/"
      }}>{`fetch`}</a>{` for more details.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/web-service-call-js-sample"
      }}>{`web-service-call-js-sample`}</a>{`
for an example plugin that communicates with a web server.`}</p>
    <h2 {...{
      "id": "oauth-workflows"
    }}>{`OAuth Workflows`}</h2>
    <p>{`You can use OAuth to connect to any supporting service that supports it, and provide online functionality in your plugin.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/oauth-workflow-sample"
      }}>{`oauth-workflow-sample`}</a>{` to see it in action.`}</p>
    <h2 {...{
      "id": "cross-compatible-javascript"
    }}>{`Cross Compatible JavaScript`}</h2>
    <p>{`UXP, as a unified platform, helps you write plugins for multiple Adobe products. Using `}<inlineCode parentName="p">{`require("uxp").host`}</inlineCode>{` you can grab the current host
application's name, and alter your code accordingly. This way, you can write a plugin once, and use it in multiple applications.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/cross-compatible-js-sample"
      }}>{`cross-compatible-js-sample`}</a>{` to see it in action.`}</p>
    <h2 {...{
      "id": "external-processes"
    }}>{`External Processes`}</h2>
    <p>{`Currently, we do not support external objects like ExtendScript. However, you can use websockets in UXP to communicate with other
applications and processes. For an example, see `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/desktop-helper-sample"
      }}>{`desktop-helper-sample`}</a>{` and
`}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/io-websocket-sample"
      }}>{`io-websocket-sample`}</a></p>
    <p>{`UXP also supports WebAssembly. See `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/wasm-rust-sample"
      }}>{`wasm-rust-sample`}</a>{` for an example project
that compiles Rust code into WASM and runs it via the UXP plugin.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      